import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ArrowRight, Loader2, Search } from "lucide-react";
import { useSite } from "../components/SiteLayout";
import { fetchPublicCourses } from "../lib/site";

export default function SiteCourses() {
  useSite();
  const [courses, setCourses] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  useEffect(() => {
    fetchPublicCourses().then(setCourses).catch(() => setCourses([]));
  }, []);

  const categories = ["All", ...new Set((courses || []).map((c) => c.category).filter(Boolean))];
  const query = q.trim().toLowerCase();
  const filtered = (courses || []).filter((c) => {
    if (cat !== "All" && (c.category || "Other Courses") !== cat) return false;
    if (!query) return true;
    return [c.name, c.short_name, c.description, c.course_type]
      .filter(Boolean)
      .some((v) => v.toLowerCase().includes(query));
  });

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="site-courses">
      <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold mb-1">Our Courses</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mb-2">Courses & Programs</h1>
      <p className="text-slate-500 text-sm sm:text-base mb-6 max-w-2xl">
        Professional certificate and diploma programs with recognized certification.
      </p>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-8">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="course-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search courses — e.g., DCA, Tally, Web Designing…"
            className="w-full h-10 pl-9 pr-3 rounded-md border border-slate-200 bg-white text-sm outline-none focus:border-blue-400"
          />
        </div>
        {categories.length > 1 && (
          <div className="flex gap-1.5 flex-wrap" data-testid="course-categories">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`course-cat-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                onClick={() => setCat(c)}
                className={`px-3.5 h-9 rounded-full text-xs font-medium transition-soft ${
                  cat === c
                    ? "bg-blue-800 text-white"
                    : "bg-white border border-slate-200 text-slate-600 hover:border-blue-300"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}
      </div>

      {courses === null ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="animate-spin inline" size={22} /></div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-slate-400 text-sm" data-testid="courses-empty">
          {q || cat !== "All"
            ? "No courses match your search."
            : "No courses are currently listed. Please check back soon."}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="courses-list">
          {filtered.map((c) => {
            const t1 = (c.subjects || []).filter((s) => s.term === 1).length;
            const t2 = (c.subjects || []).filter((s) => s.term === 2).length;
            return (
              <Link
                key={c.id}
                to={`/courses/${c.id}`}
                data-testid={`course-card-${c.id}`}
                className="rounded-xl border border-slate-200 bg-white p-0 overflow-hidden hover:border-blue-300 hover:shadow-md transition-soft flex flex-col"
              >
                {c.image ? (
                  <img src={c.image} alt={c.name} className="w-full h-36 object-cover" data-testid={`course-img-${c.id}`} />
                ) : (
                  <div className="w-full h-36 bg-gradient-to-br from-blue-900 to-blue-700 grid place-items-center text-white">
                    <BookOpen size={30} />
                  </div>
                )}
                <div className="p-6 flex flex-col flex-1">
                <div className="font-display text-lg font-semibold text-slate-900">{c.name}</div>
                <div className="text-sm text-slate-500 mt-1">Duration: {c.duration}</div>
                {c.course_type && (
                  <span className="self-start mt-2 text-[11px] uppercase tracking-wide bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                    {c.course_type}
                  </span>
                )}
                {c.description && (
                  <p className="text-sm text-slate-500 mt-3 line-clamp-2">{c.description}</p>
                )}
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                  <span>
                    {(c.subjects || []).length} subjects{t1 ? ` · T1: ${t1}` : ""}{t2 ? ` · T2: ${t2}` : ""}
                  </span>
                  <span className="inline-flex items-center gap-1 text-blue-800 font-medium" data-testid={`course-details-link-${c.id}`}>
                    View Details <ArrowRight size={13} />
                  </span>
                </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
